import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RuleFromTo } from "./restaurant.tipssharing.item";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Heading } from "@/components/ui/heading";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PaymentRuleDataSchema } from "@/utils/payment.rule.scheme";
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteInputField,
  AutoCompleteInputItem,
  AutoCompleteMenu,
  AutoCompleteMenuItem,
  AutoCompleteMenuItemType,
} from "@/screens/common/auto.complete";
import { VStack } from "@/components/ui/vstack";
import {
  Radio,
  RadioGroup,
  RadioIcon,
  RadioIndicator,
  RadioLabel,
} from "@/components/ui/radio";
import { CircleIcon, CloseIcon, Icon } from "@/components/ui/icon";
import { AlertTriangle, Trash2 } from "lucide-react-native";
import { getName } from "@/utils/name";
import { Input, InputField } from "@/components/ui/input";
import { Keyboard } from "react-native";
import {
  PaymentVersionedRule,
  PaymentVersionedRuleFromTo,
  RuleFromToDataProps,
} from "./restaurant.tipssharing.types";
import { Box } from "@/components/ui/box";

const PaymentRuleSchema = z.union([
  PaymentRuleDataSchema.shape.distribution.shape.byOrganization.element,
  PaymentRuleDataSchema.shape.distribution.shape.byParticipant.element,
  PaymentRuleDataSchema.shape.distribution.shape.byTag.element,
]);

type PaymentRuleSchemaType = z.infer<typeof PaymentRuleSchema>;

export function RestaurantTipsSharingCreateEditModal({
  rule,
  isOpen,
  onClose,
  onChange,
  onDelete,
  preferredFromType,
  ...props
}: RuleFromToDataProps &
  Pick<InterfaceModalProps, "isOpen" | "onClose"> & {
    rule?: PaymentVersionedRule;
    preferredFromType?: PaymentVersionedRuleFromTo["type"];
    onChange: (
      rule: Omit<PaymentVersionedRule, "id"> & { id?: string }
    ) => void;
    onDelete: (id: string) => void;
  }) {
  const defaultValues = useMemo(
    () =>
      ({
        from: rule?.from as unknown as PaymentRuleSchemaType["from"],
        percent: rule?.percent ?? 0,
        to: rule?.to as unknown as PaymentRuleSchemaType["to"],
      } as Partial<PaymentRuleSchemaType>),
    [rule?.from, rule?.percent, rule?.to]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<PaymentRuleSchemaType>({
    resolver: zodResolver(PaymentRuleSchema),
    defaultValues,
  });

  const [fromValue, toValue] = useWatch({ control, name: ["from", "to"] });

  useEffect(() => {
    reset(defaultValues);
  }, [rule]);

  useEffect(() => {
    if (fromValue?.type === "tag" && toValue?.type === "tag") {
      setValue("to", "" as any);
    }
  }, [fromValue?.type]);

  const onSubmit = (data: PaymentRuleSchemaType) => {
    onChange({
      ...data,
      id: rule?.id,
    });

    reset(defaultValues);
    onClose();
  };

  const handleDelete = () => {
    if (rule) {
      onDelete(rule.id);
    }

    onClose();
  };

  return (
    <Modal avoidKeyboard isOpen={isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>{rule ? "Edit rule" : "Add Rule"}</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl isInvalid={!!errors?.from} className="w-full" isRequired>
            <FormControlLabel>
              <FormControlLabelText>From</FormControlLabelText>
            </FormControlLabel>
            <Controller
              name="from"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <FromToRuleEditBox
                  {...props}
                  fromTo={value}
                  preset={{
                    isFrom: true,
                    preferredType: preferredFromType,
                    items: [
                      {
                        type: "participant",
                        title: "Worker QR-code",
                      },
                      {
                        type: "organization",
                        title: "Business or custom QR code",
                      },
                      {
                        type: "tag",
                        title: "Tag",
                      },
                    ],
                  }}
                  onChange={(newVal) => {
                    onChange(newVal ?? "");
                  }}
                  onBlur={onBlur}
                />
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.from?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl
            isInvalid={!!errors?.percent}
            className="w-full"
            isRequired
          >
            <FormControlLabel>
              <FormControlLabelText>Percentage</FormControlLabelText>
            </FormControlLabel>
            <Controller
              name="percent"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input>
                  <InputField
                    autoCorrect={false}
                    autoComplete="off"
                    autoCapitalize="none"
                    keyboardType="number-pad"
                    placeholder="Percentage of tips"
                    selectTextOnFocus
                    value={value.toString()}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onSubmitEditing={() => Keyboard.dismiss()}
                    returnKeyType="done"
                  />
                </Input>
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.percent?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.to} className="w-full" isRequired>
            <FormControlLabel>
              <FormControlLabelText>To</FormControlLabelText>
            </FormControlLabel>
            <Controller
              name="to"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <FromToRuleEditBox
                  {...props}
                  fromTo={value}
                  preset={{
                    isFrom: false,
                    items: [
                      {
                        type: "participant",
                        title: "Worker wallet",
                      },
                      {
                        type: "wallet",
                        title: "Business wallet",
                      },
                      ...(fromValue && fromValue.type !== "tag"
                        ? [
                            {
                              type: "tag" as const,
                              title: "Tag",
                            },
                          ]
                        : []),
                    ],
                  }}
                  onChange={(newVal) => {
                    onChange(newVal ?? "");
                  }}
                  onBlur={onBlur}
                />
              )}
            />
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>{errors?.to?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-row justify-between">
          {rule ? (
            <Button
              className="gap-2"
              variant="outline"
              action="negative"
              onPress={handleDelete}
            >
              <ButtonIcon as={Trash2} />
              {isSubmitting && <ButtonSpinner />}
            </Button>
          ) : (
            <Box className="grow" />
          )}
          <Button
            className="gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>{rule ? "Save rule" : "Add Rule"}</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function PaymentRuleBox(
  props: PropsWithChildren<{
    onChange?: () => void;
  }>
) {
  return (
    <Card
      size="sm"
      variant="filled"
      className="flex-row w-full items-center justify-between"
    >
      <Box className="flex-1">{props.children}</Box>
      {props.onChange && (
        <Button size="xs" variant="outline" onPress={props.onChange}>
          <ButtonText>Change</ButtonText>
        </Button>
      )}
    </Card>
  );
}

type PaymentRuleSchemaFromToType =
  | PaymentRuleSchemaType["from"]
  | PaymentRuleSchemaType["to"];

type FromToRadioOption = {
  type: PaymentRuleSchemaFromToType["type"];
  title: string;
};

type FromToAutoCompleteMenuItemType = AutoCompleteMenuItemType & {
  title: string;
  titleNode?: ReactNode;
  rule: PaymentRuleSchemaFromToType;
};

function FromToRuleEditBox({
  fromTo,
  onChange,
  onBlur,
  preset,
  ...props
}: RuleFromToDataProps & {
  fromTo?: PaymentRuleSchemaFromToType;
  preset: {
    isFrom: boolean;
    preferredType?: PaymentRuleSchemaFromToType["type"];
    items: Array<FromToRadioOption>;
  };
  onChange: (newValue: PaymentRuleSchemaFromToType | undefined) => void;
  onBlur: () => void;
}) {
  const [selectedType, setSelectedType] = useState<
    PaymentRuleSchemaFromToType["type"] | undefined
  >(fromTo?.type ?? preset.preferredType);

  useEffect(() => {
    if (fromTo?.type !== selectedType) {
      setSelectedType(fromTo?.type ?? preset.preferredType);
    }
  }, [fromTo?.type]);

  const selectedValue = useMemo<
    FromToAutoCompleteMenuItemType | undefined
  >(() => {
    if (!fromTo) {
      return undefined;
    }

    switch (fromTo.type) {
      case "organization":
        return {
          id: fromTo.qrId ?? "__main__",
          title: fromTo.qrId
            ? props.qrs.find((qr) => qr.id === fromTo.qrId)?.internalCode ??
              "Unknown QR-code"
            : "Main QR-code",
          rule: fromTo,
        };
      case "participant":
        return fromTo.id !== undefined
          ? {
              id: fromTo.id,
              title: getName(
                props.participants.find((p) => p.userId === fromTo.id)?.user
              ),
              rule: fromTo,
            }
          : undefined;
      case "tag":
        return fromTo.tag !== undefined
          ? {
              id: fromTo.tag,
              title: fromTo.tag,
              rule: fromTo,
            }
          : undefined;
      case "wallet":
        return fromTo.id !== undefined
          ? {
              id: fromTo.id,
              title:
                props.wallets.find((w) => w.id === fromTo.id)?.name ??
                "Unknown wallet",
              rule: fromTo,
            }
          : undefined;
    }
  }, [fromTo]);

  const placeholder = useMemo(() => {
    switch (selectedType) {
      case "organization":
        return "Choose QR-code";
      case "participant":
        return "Choose worker";
      case "tag":
        return "Choose tag";
      case "wallet":
        return "Choose wallet";
      default:
        return "Select type first";
    }
  }, [selectedType]);

  const uniqueTags = useMemo(() => {
    const uniqueSet = new Set(props.tags?.public);
    props.tags?.private.forEach(uniqueSet.add.bind(uniqueSet));

    return [...uniqueSet];
  }, [props.tags?.public, props.tags?.private]);

  const items = useMemo<FromToAutoCompleteMenuItemType[]>(() => {
    if (!selectedType) {
      return [];
    }

    switch (selectedType) {
      case "organization":
        return [
          ...(preset.isFrom
            ? [
                {
                  id: "__main__",
                  title: "Main QR-code",
                  titleNode: <Text bold>Main QR-code</Text>,
                  rule: { type: "organization" as const },
                },
              ]
            : []),
          ...props.qrs.map<FromToAutoCompleteMenuItemType>((qr) => ({
            id: qr.id,
            title: qr.internalCode,
            rule: { type: "organization", qrId: qr.id },
          })),
        ];
      case "participant":
        return [
          ...(preset.isFrom
            ? [
                {
                  id: "__any__",
                  title: "Any worker",
                  titleNode: <Text bold>Any worker</Text>,
                  rule: { type: "participant" as const, id: "__any__" },
                },
              ]
            : []),
          ...props.participants.map<FromToAutoCompleteMenuItemType>((p) => ({
            id: p.id,
            title: getName(p.user),
            rule: { type: "participant", id: p.id },
          })),
        ];
      case "tag":
        return [
          ...uniqueTags.map<FromToAutoCompleteMenuItemType>((t) => ({
            id: t,
            title: t,
            rule: { type: "tag", tag: t },
          })),
        ];
      case "wallet":
        return [
          ...props.wallets.map<FromToAutoCompleteMenuItemType>((w) => ({
            id: w.id,
            title: w.name ?? `Wallet ${w.currency}`,
            rule: { type: "wallet", id: w.id },
          })),
        ];
    }
  }, [
    selectedType,
    props.organization,
    props.participants,
    props.qrs,
    props.tags,
    props.wallets,
  ]);

  if (fromTo) {
    return (
      <PaymentRuleBox onChange={() => onChange(undefined)}>
        <RuleFromTo {...props} fromTo={fromTo} />
      </PaymentRuleBox>
    );
  }

  return (
    <VStack className="gap-2">
      <RadioGroup
        value={selectedType ?? ""} // if pass undefined, warning uncontrolled -> controlled will be thrown
        onChange={(type: PaymentRuleSchemaFromToType["type"]) =>
          setSelectedType(type)
        }
      >
        <VStack className="gap-1">
          {preset.items.map((t) => (
            <Radio key={t.type} size="sm" value={t.type}>
              <RadioIndicator>
                <RadioIcon as={CircleIcon} />
              </RadioIndicator>
              <RadioLabel>{t.title}</RadioLabel>
            </Radio>
          ))}
        </VStack>
      </RadioGroup>
      <AutoComplete<FromToAutoCompleteMenuItemType>
        filter={(items, val) =>
          items.filter((i) => i.title.toLowerCase().includes(val.toLowerCase()))
        }
        value={selectedValue ? [selectedValue] : []}
        onChange={(newValues) => {
          onChange(newValues[0].rule);
        }}
        items={items}
      >
        <AutoCompleteInput<FromToAutoCompleteMenuItemType>
          isDisabled={!selectedType}
          renderItem={(value, props) => (
            <AutoCompleteInputItem
              text={value.item.title}
              action="info"
              {...props}
            />
          )}
        >
          <AutoCompleteInputField placeholder={placeholder} onBlur={onBlur} />
        </AutoCompleteInput>
        <AutoCompleteMenu<FromToAutoCompleteMenuItemType>
          renderItem={(info, props) => (
            <AutoCompleteMenuItem
              {...(info.item.titleNode
                ? { node: info.item.titleNode }
                : { text: info.item.title })}
              {...props}
            />
          )}
        ></AutoCompleteMenu>
      </AutoComplete>
    </VStack>
  );
}
